import { createSettingsParams, SettingsParamType } from '@wix/tpa-settings';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

import { withAiDescription } from '../../../../../components-shared/Settings/withAiDescription';

export type DisplaySettingsParams = {
  showHeaderImage: SettingsParamType.Boolean;
  showHeaderTitle: SettingsParamType.Boolean;
  showHeaderDescription: SettingsParamType.Boolean;
  showMenuDescription: SettingsParamType.Boolean;
  showSectionTitle: SettingsParamType.Boolean;
  showSectionDescription: SettingsParamType.Boolean;
  showSectionImage: SettingsParamType.Boolean;
  showMenuDivider: SettingsParamType.Boolean;
  showItemImage: SettingsParamType.Boolean;
  showItemDescription: SettingsParamType.Boolean;
  showItemLabels: SettingsParamType.Boolean;
  showItemFeatured: SettingsParamType.Boolean;
  showAddToCartButton: SettingsParamType.Boolean;
  overrideItemDesign: SettingsParamType.Boolean;
};

/**
 * `metadata` exposes these params in the widget's editor manifest
 * (`editorElement.data`), which is what Aria reads and edits in Studio 2.
 * Params without `metadata` are dropped from the manifest, so Aria can't
 * change them. `displayName` reuses the Settings panel labels, and
 * `withAiDescription` gives each param one description for both Aria versions.
 */
export const displaySettingsParams =
  createSettingsParams<DisplaySettingsParams>({
    showHeaderImage: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.header-image'),
          description:
            'Shows (true) or hides (false) the header cover image. ' +
            "Hiding doesn't delete it; headerImagePlacement has no effect while hidden. " +
            'To hide the whole header, set showHeaderImage, showHeaderTitle and ' +
            'showHeaderDescription to false. The location button and order status ' +
            "badges can't be hidden.",
        }),
      }),
    },
    showHeaderTitle: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.header-title'),
          description:
            'Shows (true) or hides (false) the title at the top of the Online Orders widget ' +
            'header (default text "Online Ordering"). This is the only way to hide or show ' +
            'the title; clearing headerTitle does not hide it.',
        }),
      }),
    },
    showHeaderDescription: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.header-description'),
          description:
            'Shows (true) or hides (false) the description text under the title in the ' +
            'Online Orders widget header. This is the only way to hide or show the ' +
            'description; clearing headerDescription does not hide it.',
        }),
      }),
    },
    showMenuDescription: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.menu-description'),
          description:
            'Shows (true) or hides (false) the description shown under each menu name.',
        }),
      }),
    },
    showSectionTitle: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.section-title'),
          description:
            'Shows (true) or hides (false) the title of each menu section (for example ' +
            '"Appetizers").',
        }),
      }),
    },
    showSectionDescription: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.section-description'),
          description:
            'Shows (true) or hides (false) the description shown under each menu section ' +
            'title.',
        }),
      }),
    },
    showSectionImage: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.section-image'),
          description:
            'Shows (true) or hides (false) the cover image of each menu section, when the ' +
            'section has one.',
        }),
      }),
    },
    showMenuDivider: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.menu-divider'),
          description:
            'Shows (true) or hides (false) the divider line between the menu name and its ' +
            'sections.',
        }),
      }),
    },
    showItemImage: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.item-image'),
          description:
            'Shows (true) or hides (false) the image of each menu item, when the item has ' +
            'one.',
        }),
      }),
    },
    showItemDescription: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.item-description'),
          description:
            'Shows (true) or hides (false) the description of each menu item.',
        }),
      }),
    },
    showItemLabels: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.item-labels'),
          description:
            'Shows (true) or hides (false) the dietary label icons of each menu item (for ' +
            'example vegan or spicy).',
        }),
      }),
    },
    showItemFeatured: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.design.section-header.highlighted-item'),
          description:
            'Shows (true) or hides (false) the highlighted (featured) badge on menu items ' +
            'marked as featured. When false, featured items also lose the highlighted item ' +
            'design set by overrideItemDesign.',
        }),
      }),
    },
    showAddToCartButton: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ...withAiDescription({
          displayName: t('app.settings.display.add-to-cart-button'),
          description:
            'Shows (true) or hides (false) the add to cart button on menu items. When ' +
            'hidden, customers open the item to add it to the cart. This is the only way to ' +
            'hide the button; clearing addToCartButtonText does not hide it. Has no effect ' +
            'on sites where the quick add to cart button is not available.',
        }),
      }),
    },
    overrideItemDesign: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => false,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: false },
        ...withAiDescription({
          displayName: t(
            'app.settings.design.highlighted-item.override-item-design',
          ),
          description:
            'When true, highlighted (featured) menu items use their own custom design ' +
            '(background, borders, corners, colors and fonts) instead of the regular item ' +
            'design. Only has an effect when showItemFeatured is true. Default false.',
        }),
      }),
    },
  });
